// src/components/BankPosting/components/BankPostingRemovedItems.jsx
// ============================================================================
// BLOCK BP.CMP.004 — Removed items panel
// ----------------------------------------------------------------------------
// PURPOSE
// - Displays removed/ignored bank transaction rows in grouped date sections
// - Supports two separate actions:
//     1. restore selected row(s) back to active queue
//     2. mark checked removed rows for final clear operation
// - Keeps checkbox meaning narrow: checked = included in clear action only
//
// USER EXPERIENCE
// - Clicking a row does not restore or mutate anything
// - Checking a row stages it for an explicit action button below
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ============================================================================
// BLOCK BP.CMP.004.01 — Main removed-items panel component
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders grouped removed rows
// - Keeps removed rows read-only until the user chooses an explicit action
// - Renders bottom action buttons for restore and clear flows
// ============================================================================
export default function BankPostingRemovedItems({
  groups,
  selectedRemovedStateIds,
  onRestore,
  onToggleCleared,
  onToggleAllCleared,
  onClearRemoved,
}) {
  const visibleStateIds = React.useMemo(
    () =>
      groups
        .flatMap((group) => (Array.isArray(group?.transactions) ? group.transactions : []))
        .map((row) => Number(row?.id))
        .filter(Number.isFinite),
    [groups]
  );

  const selectedVisibleCount = React.useMemo(() => {
    const visibleIdSet = new Set(visibleStateIds);
    return selectedRemovedStateIds.reduce(
      (count, id) => (visibleIdSet.has(Number(id)) ? count + 1 : count),
      0
    );
  }, [selectedRemovedStateIds, visibleStateIds]);

  const allVisibleChecked =
    visibleStateIds.length > 0 && selectedVisibleCount === visibleStateIds.length;
  const someVisibleChecked = selectedVisibleCount > 0 && !allVisibleChecked;

  return (
    <Box>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          minHeight: 140,
          p: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Items Removed (ignored)
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Checkbox
              size="small"
              checked={allVisibleChecked}
              indeterminate={someVisibleChecked}
              disabled={!visibleStateIds.length}
              onChange={(e) => onToggleAllCleared(visibleStateIds, e.target.checked)}
              inputProps={{ 'aria-label': 'Select all removed items' }}
            />
            <Typography variant="caption" color="text.secondary">
              Select all
            </Typography>
          </Stack>
        </Stack>

        <List dense disablePadding sx={{ maxHeight: 240, overflowY: 'auto', overflowX: 'hidden' }}>
          {groups.map((group) => (
            <Box key={group.date} sx={{ mb: 1 }}>
              {/* Date bucket header for removed rows */}
              <Typography variant="body2" fontWeight={700}>
                {group.date}
              </Typography>

              {group.transactions.map((row) => {
                const checked = selectedRemovedStateIds.includes(Number(row.id));

                return (
                  <Box
                    key={row.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 40,
                      pr: 1,
                    }}
                  >
                    {/*
                      Checkbox meaning:
                      - checked => include in a later explicit action
                      - not checked => leave row alone
                    */}
                    <Checkbox
                      edge="start"
                      checked={checked}
                      onChange={(e) => onToggleCleared(row, e.target.checked)}
                    />
                    <ListItemText
                      primary={`${row.description} - $${Number(row.amount || 0).toFixed(2)}`}
                      secondary={row.date}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}
        </List>
      </Box>

      {/* Bottom actions for removed-item workflows */}
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          onClick={() => onRestore(selectedRemovedStateIds)}
          disabled={!selectedRemovedStateIds.length}
        >
          Move Selected back to top
        </Button>
        <Button variant="contained" onClick={onClearRemoved}>
          Remove Cleared Items
        </Button>
      </Stack>
    </Box>
  );
}

BankPostingRemovedItems.propTypes = {
  groups: PropTypes.array.isRequired,
  selectedRemovedStateIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onRestore: PropTypes.func.isRequired,
  onToggleCleared: PropTypes.func.isRequired,
  onToggleAllCleared: PropTypes.func.isRequired,
  onClearRemoved: PropTypes.func.isRequired,
};
